import { COMPONENT, FPTI_CONSTANTS, FPTI_ACTION, SPACE_KEY } from './constants';
import clientLogger from './logger';

let FPTI;

// FPTI Variables & Metrics
// https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=90989206
const isInitialized = () => {
  FPTI = window.PAYPAL?.analytics?.instance;
  if (FPTI !== undefined) {
    return true;
  }
  clientLogger.cal.warn('fpti uninitialized');
  return false;
};

const getEventName = (component, action) => {
  return `${FPTI_CONSTANTS.EVENT_PREFIX}_${window.fpti.page?.split('?')[0]}_${component}_${action}`;
};

const getTempVars = (options) => {
  const componentTags = {
    button_label: options.buttonName,
    link_name: options.linkName,
    redirect_url: options.redirectUrl,
    tab_name: options.tabName,
    section_name: options.sectionName,
    modal_name: options.modalName,
    field_name: options.fieldName,
    element_name: options.elementName,
    optsel: options.optSel,
    content: options.content
  };
  const genericTags = {
    event_name: options.eventName,
    error_message: options.errorMessage
  };
  return {
    ...componentTags,
    ...genericTags
  };
};

export const updatePersistentVars = () => {
  const commonTags = {
    comp: COMPONENT,
    space_key: SPACE_KEY
  };
  Object.assign(window.fpti, commonTags);
};

const trackEvent = (options = {}) => {
  updatePersistentVars();
  if (isInitialized()) {
    FPTI.recordEvent({
      data: getTempVars(options)
    });
  }
};

const trackClick = (options = {}) => {
  updatePersistentVars();
  if (isInitialized()) {
    FPTI.recordClick({
      data: getTempVars(options)
    });
  }
};

const trackImpression = (options = {}) => {
  updatePersistentVars();
  if (isInitialized()) {
    FPTI.recordImpression({
      data: getTempVars(options)
    });
  }
};

export const trackButtonClick = (buttonName, redirectUrl, content) => {
  const eventName = getEventName(FPTI_CONSTANTS.BUTTON, FPTI_ACTION.PRESSED);
  trackClick({
    buttonName,
    eventName,
    redirectUrl,
    content
  });
};

export const trackButtonChange = (buttonName, optSel) => {
  const eventName = getEventName(FPTI_CONSTANTS.BUTTON, FPTI_ACTION.CHANGED);
  trackClick({
    buttonName,
    eventName,
    optSel
  });
};

export const trackFieldFocus = (fieldName) => {
  const eventName = getEventName(FPTI_CONSTANTS.FIELD, FPTI_ACTION.FOCUS);
  trackClick({
    fieldName,
    eventName
  });
};

export const trackFieldChange = (fieldName, optSel) => {
  const eventName = getEventName(FPTI_CONSTANTS.FIELD, FPTI_ACTION.CHANGED);
  trackClick({
    fieldName,
    eventName,
    optSel
  });
};

export const trackFieldClosed = (fieldName) => {
  const eventName = getEventName(FPTI_CONSTANTS.FIELD, FPTI_ACTION.CLOSED);
  trackClick({
    fieldName,
    eventName
  });
};

export const trackLinkClick = (linkName, redirectUrl) => {
  const eventName = getEventName(FPTI_CONSTANTS.LINK, FPTI_ACTION.PRESSED);
  trackClick({
    linkName,
    eventName,
    redirectUrl
  });
};

export const trackTabClick = (tabName) => {
  const eventName = getEventName(FPTI_CONSTANTS.TAB, FPTI_ACTION.PRESSED);
  trackClick({
    tabName,
    eventName
  });
};

export const trackCopyCodeTab = (buttonName, tabName) => {
  const eventName = getEventName(FPTI_CONSTANTS.BUTTON, FPTI_ACTION.PRESSED);
  trackClick({
    buttonName,
    tabName,
    eventName
  });
};

export const trackKeyboardCopy = (content, sectionName) => {
  const eventName = getEventName(FPTI_CONSTANTS.KEYBOARD, FPTI_ACTION.COPY);
  trackClick({
    content,
    sectionName,
    eventName
  });
};

export const trackTabShown = (tabName) => {
  const eventName = getEventName(FPTI_CONSTANTS.TAB, FPTI_ACTION.SHOWN);
  trackImpression({
    tabName,
    eventName
  });
};

export const trackPageShown = (pageName) => {
  const eventName = getEventName(FPTI_CONSTANTS.PAGE, FPTI_ACTION.SHOWN);
  trackImpression({
    pageName,
    eventName
  });
};

export const trackPageFocus = (pageName) => {
  const eventName = getEventName(FPTI_CONSTANTS.PAGE, FPTI_ACTION.FOCUS);
  trackImpression({
    pageName,
    eventName
  });
};

export const trackPageLostFocus = (pageName) => {
  const eventName = getEventName(FPTI_CONSTANTS.PAGE, FPTI_ACTION.BLUR);
  trackImpression({
    pageName,
    eventName
  });
};

export const trackModalShown = (modalName) => {
  const eventName = getEventName(FPTI_CONSTANTS.MODAL, FPTI_ACTION.SHOWN);
  trackImpression({
    modalName,
    eventName
  });
};

export const trackModalClosed = (modalName) => {
  const eventName = getEventName(FPTI_CONSTANTS.MODAL, FPTI_ACTION.CLOSED);
  trackClick({
    modalName,
    eventName
  });
};

export const trackModalError = (modalName, errorMessage) => {
  const eventName = getEventName(FPTI_CONSTANTS.MODAL, FPTI_ACTION.ERROR);
  trackEvent({
    modalName,
    errorMessage,
    eventName
  });
};

export const trackElementFocus = (elementName) => {
  const eventName = getEventName(FPTI_CONSTANTS.ELEMENT, FPTI_ACTION.FOCUS);
  trackEvent({
    elementName,
    eventName
  });
};

export const trackElementError = (elementName, errorMessage) => {
  const eventName = getEventName(FPTI_CONSTANTS.ELEMENT, FPTI_ACTION.ERROR);
  trackEvent({
    elementName,
    errorMessage,
    eventName
  });
};

export const trackElementExit = (elementName) => {
  const eventName = getEventName(FPTI_CONSTANTS.ELEMENT, FPTI_ACTION.BLUR);
  trackEvent({
    elementName,
    eventName
  });
};
